// annual-review-automation.js - Tier 3 Solution Page
export const content = {
  heroSubtitle: "Annual reviews are your best retention defense—but only if they actually happen. Automate the scheduling, preparation, and follow-up so every client gets the attention they deserve.",

  problemSection: {
    title: "The Annual Review Reality",
    html: `
      <p>You know annual reviews are important. You intend to do them with every client. But then AEP hits and you're scrambling to write new business while somehow also reviewing your entire book. Some clients get thorough reviews. Others get a quick call. Many get nothing at all.</p>

      <p>The clients who don't get proper reviews? They're exactly the ones who end up switching—often because no one took the time to address their questions, update their information, or reinforce why their current plan still works.</p>

      <p>Annual review automation solves this by making reviews systematic instead of chaotic. Scheduling happens automatically. Preparation materials are generated. Follow-up is tracked. Every client gets the attention the relationship deserves.</p>
    `
  },

  features: [
    {
      icon: "clock",
      title: "Smart Scheduling",
      description: "System schedules reviews based on optimal timing: before ANOC window, avoiding your busy periods, respecting client preferences. Calendar integration ensures nothing conflicts."
    },
    {
      icon: "chart",
      title: "Automated Preparation",
      description: "Review packet generated for each client: current plan summary, changes coming, risk factors, notes from last review, and recommended discussion points."
    },
    {
      icon: "bell",
      title: "Reminder Sequences",
      description: "Automated appointment reminders to clients. Reschedule prompts if they miss. No more chasing people who forgot about scheduled reviews."
    },
    {
      icon: "target",
      title: "Prioritized Queues",
      description: "Not all reviews are equal priority. High-risk clients and those with significant plan changes are prioritized automatically so critical reviews happen first."
    },
    {
      icon: "check",
      title: "Completion Tracking",
      description: "Dashboard shows review status across your book: completed, scheduled, pending, overdue. Know exactly where you stand without manual tracking."
    },
    {
      icon: "shield",
      title: "Documentation",
      description: "Review notes and decisions automatically captured and stored. Complete audit trail of what was discussed and what was decided."
    }
  ],

  featuresTitle: "How Review Automation Works",

  mainContent: `
    <h2>Why Annual Reviews Matter</h2>

    <p>The annual review is your primary retention event. It's when you:</p>
    <ul>
      <li>Confirm their current plan still fits their needs</li>
      <li>Discuss any changes coming for the new year</li>
      <li>Address questions and concerns</li>
      <li>Update your records with life changes</li>
      <li>Reinforce the value of your relationship</li>
      <li>Demonstrate you're paying attention</li>
    </ul>

    <p>A client who has had a thorough annual review feels served. They're not wondering if they should shop around—they just did the comparison with you and decided to stay. They're not vulnerable to competitor marketing because they already know their plan is the right choice.</p>

    <p>A client who didn't get a review? They're doing exactly what you should have helped them do—but with another agent.</p>

    <h2>The Problem With Manual Reviews</h2>

    <p>Without automation, annual reviews suffer from:</p>

    <h3>Scheduling Chaos</h3>
    <ul>
      <li>Reviews pile up in October (worst possible timing)</li>
      <li>Conflicts with new business writing</li>
      <li>Some clients never get scheduled</li>
      <li>Rescheduling falls through the cracks</li>
    </ul>

    <h3>Inconsistent Preparation</h3>
    <ul>
      <li>Some reviews are thorough, others are rushed</li>
      <li>No standard agenda or talking points</li>
      <li>Important information isn't pulled in advance</li>
      <li>Plan changes aren't researched before the call</li>
    </ul>

    <h3>Poor Follow-Through</h3>
    <ul>
      <li>Decisions made but not documented</li>
      <li>Action items forgotten</li>
      <li>Next-year review not scheduled</li>
      <li>Incomplete records for next time</li>
    </ul>

    <h2>The Automated Annual Review Process</h2>

    <h3>Phase 1: Scheduling (August-September)</h3>

    <p>The system automatically:</p>
    <ul>
      <li>Identifies all clients due for annual review</li>
      <li>Calculates optimal timing based on risk level and availability</li>
      <li>Proposes schedule respecting your preferences</li>
      <li>Sends appointment invitations to clients</li>
      <li>Handles confirmations and rescheduling</li>
    </ul>

    <p>You approve the schedule and let the system handle the logistics.</p>

    <h3>Phase 2: Preparation (Before Each Review)</h3>

    <p>Before each appointment, you receive:</p>
    <ul>
      <li><strong>Client summary:</strong> Current coverage, tenure, key details</li>
      <li><strong>Plan change report:</strong> What's changing for the new year</li>
      <li><strong>Risk assessment:</strong> Factors that need addressing</li>
      <li><strong>Notes from last review:</strong> What was discussed, what was decided</li>
      <li><strong>Recommended talking points:</strong> What to cover based on their situation</li>
    </ul>

    <p>You walk into every review prepared, not scrambling to remember who this client is.</p>

    <h3>Phase 3: Conducting the Review</h3>

    <p>During the review, the system provides:</p>
    <ul>
      <li>Structured agenda to follow</li>
      <li>Plan comparison tools if considering alternatives</li>
      <li>Easy note-taking interface</li>
      <li>Action item capture</li>
    </ul>

    <h3>Phase 4: Follow-Up (After Each Review)</h3>

    <p>After the review, automatically:</p>
    <ul>
      <li>Summary email sent to client</li>
      <li>Action items created as tasks</li>
      <li>Review notes saved to client record</li>
      <li>Next review scheduled (or review cycle reset)</li>
    </ul>

    <h2>Prioritization Logic</h2>

    <p>Not all reviews have equal urgency. The system prioritizes:</p>

    <h3>Highest Priority</h3>
    <ul>
      <li>High-risk clients (elevated churn probability)</li>
      <li>Clients with significant plan changes</li>
      <li>First-year clients (building relationship foundation)</li>
      <li>Clients with unresolved issues</li>
    </ul>

    <h3>Standard Priority</h3>
    <ul>
      <li>Established clients with minor changes</li>
      <li>Stable plans and stable relationships</li>
      <li>Low-risk scores</li>
    </ul>

    <p>Critical reviews happen first, ensuring you protect your most vulnerable clients even if time runs short.</p>

    <h2>The Completion Dashboard</h2>

    <p>At any point, you see exactly where you stand:</p>
    <ul>
      <li><strong>Total clients:</strong> How many need reviews</li>
      <li><strong>Completed:</strong> Reviews finished</li>
      <li><strong>Scheduled:</strong> Appointments on calendar</li>
      <li><strong>Pending:</strong> Not yet scheduled</li>
      <li><strong>Overdue:</strong> Should have been done by now</li>
    </ul>

    <p>No more wondering how many clients you've missed. The dashboard tells you.</p>

    <h2>Results You Can Measure</h2>

    <p>Agents using automated annual reviews report:</p>
    <ul>
      <li>90%+ review completion rate (vs. 50-70% manual)</li>
      <li>Significantly reduced AEP chaos</li>
      <li>Better retention rates (reviews = retention)</li>
      <li>More confident conversations (prepared materials)</li>
      <li>Complete documentation for every client</li>
    </ul>

    <p>The time invested in setting up automation pays back in smoother AEPs for years.</p>
  `,

  faqs: [
    {
      question: "How does automated scheduling work?",
      answer: "The system identifies clients due for reviews, calculates optimal timing based on risk level and your availability, sends appointment invitations, and handles confirmations and rescheduling. You approve the schedule; the system handles logistics."
    },
    {
      question: "What preparation materials are generated?",
      answer: "Before each review: client summary, plan change report for the new year, risk assessment with factors to address, notes from last review, and recommended talking points based on their situation. You walk in prepared, not scrambling."
    },
    {
      question: "How does prioritization work?",
      answer: "High-risk clients, those with significant plan changes, first-year clients, and those with unresolved issues are prioritized for earlier reviews. Critical reviews happen first, protecting your most vulnerable clients even if time runs short."
    },
    {
      question: "What happens after each review?",
      answer: "Automatic follow-up: summary email to client, action items created as tasks, review notes saved to client record, next review scheduled. Complete documentation without manual data entry."
    },
    {
      question: "What completion rates do agents typically achieve with automation?",
      answer: "Agents using automated reviews report 90%+ completion rates, compared to 50-70% with manual processes. The system ensures reviews don't fall through the cracks, even during chaotic AEP periods."
    }
  ],

  sources: [
    {
      title: "Medicare Annual Enrollment Period",
      url: "https://www.cms.gov/Medicare/Eligibility-and-Enrollment/MedicareEligEnrol",
      publisher: "CMS",
      accessedDate: "2024-12-10"
    }
  ]
};

export default content;
